<template>
  <div>
    <v-sheet outlined rounded>
      <div class="text-subtitle-1 ml-3 mt-1">{{ $t('filter_by') }}</div>
      <v-row dense class="mx-3 mt-2">
        <v-col lg="3" md="6" cols="12" v-if="showTableControl('actor')">
          <v-select
            hide-details
            :label="$t('action_type')"
            prepend-inner-icon="mdi-email"
            v-model="searchCriteria.actionType"
            :items="actionOptions"/>
        </v-col>
        <v-col lg="3" md="6" cols="12" v-if="showTableControl('actor')">
          <v-autocomplete
            hide-no-data
            hide-selected
            v-model="searchCriteria.actorEmail"
            :items="userOptions"
            :disabled="searchCriteria.actionType === 'system_actions'"
            @update:search-input="searchUsers"
            :label="$t('email')"
            prepend-inner-icon="mdi-email"/>
        </v-col>
        <v-col v-if="showTableControl('customer')" lg="3" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('customer', 2)"
            v-model="searchCriteria.customers"
            :items="customerOptions"
            prepend-inner-icon="mdi-office-building"/>
        </v-col>
        <v-col v-if="showTableControl('resource_type')" lg="3" md="6" cols="12">
          <v-select
            v-model="searchCriteria.resourceType"
            :items="resourceTypeOptions"
            prepend-inner-icon="mdi-cube"/>
        </v-col>
        <v-col v-if="showTableControl('date_range')" lg="3" md="6" cols="12">
          <div class="d-flex align-center">
            <date-picker
              v-model="searchCriteria.createdAfter"
              :timezone="customerTimezone"
              :label="$t('start_date')"
              prepend-inner-icon="mdi-calendar"
              reset-on-timezone-change/>
            <date-picker
              v-model="searchCriteria.createdBefore"
              :timezone="customerTimezone"
              :label="$t('end_date')"
              prepend-inner-icon="mdi-calendar"
              reset-on-timezone-change/>
          </div>
        </v-col>
        <v-col v-if="showTableControl('primary_key')" lg="3" md="6" cols="12">
          <v-text-field
            v-model="searchCriteria.primaryKey"
            clearable
            @keyup.enter.native="refreshResults()"
            :label="$t('primary_key')"/>
        </v-col>
        <v-col v-if="showTableControl('updated_field')" lg="3" md="6" cols="12">
          <v-text-field
            v-model="searchCriteria.updatedField"
            clearable
            :label="$t('updated_field')"
            prepend-inner-icon="mdi-bookmark"/>
        </v-col>
      </v-row>
      <div class="d-flex justify-end mr-3 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('download') }}
        </v-btn>
        <vf-tooltip :text="$t('download')">
          <v-btn class="hidden-lg-and-up" fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>

    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="mt-2 vf-table-alt"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        item-key="index"
        single-expand
        show-expand
        :item-class="rowClassCallback"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">
        <template v-slot:item.user="row">
          <div class="d-flex align-center justify-space-between">
            <span v-if="row.item.user_id != null">
              <router-link v-if="user.can('view_user', 'all')" :to="'/user/update/'+row.item.user_id">
                <v-icon color="primary">mdi-account</v-icon> {{ row.item.user.email }}
              </router-link>
              <span v-else>
                <v-icon>mdi-server</v-icon> {{ $t('user_event') }}
              </span>
            </span>
            <span v-else>
              <v-icon>mdi-server</v-icon> {{ $t('automated_event') }}
            </span>
            <v-btn
              fab
              text
              small
              color="std-icon-grey"
              class="hidden-lg-and-down"
              @click="updateActorEmailSearchCriteria(row.item.user.email)">
              <v-icon>mdi-filter</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:item.customer="row">
          <if-set :value="row.item.customer" dash>
            <template v-slot:default="scope">
              <div class="d-flex align-center justify-space-between">
                <router-link v-if="user.can('update_customer')" :to="'/customer/'+scope.value.id">
                  <v-icon color="primary">mdi-office-building</v-icon>
                  {{row.item.customer.name}}
                </router-link>
                <span v-else>
                  <v-icon>mdi-office-building</v-icon>
                  {{row.item.customer.name}}
                </span>
                <v-btn
                  fab
                  text
                  small
                  color="std-icon-grey"
                  class="hidden-lg-and-down"
                  @click="updateCustomerSearchCriteria(row.item.customer)">
                  <v-icon>mdi-filter</v-icon>
                </v-btn>
              </div>
            </template>
          </if-set>
        </template>
        <template v-slot:item.resource_type="row">
          <div class="d-flex align-center justify-space-between">
            <span>{{ $tc(row.item.resource_type) }}</span>
            <v-btn
              fab
              text
              small
              color="std-icon-grey"
              class="hidden-lg-and-down"
              @click="updateResourceTypeSearchCriteria(row.item.resource_type)">
              <v-icon>mdi-filter</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:item.primary_key="row">
          <div class="d-flex align-center justify-space-between">
            <span v-if="generateResourceLink(row.item.resource_type, row.item.primary_key)">
              <router-link :to="generateResourceLink(row.item.resource_type, row.item.primary_key)">
                <v-icon color="primary">{{ resourceIcon(row.item.resource_type) }}</v-icon>
                {{ row.item.primary_key }}
              </router-link>
            </span>
              <span v-else>
              <v-icon>{{ resourceIcon(row.item.resource_type) }}</v-icon>
              {{ row.item.primary_key }}
            </span>
            <v-btn
              fab
              text
              small
              color="std-icon-grey"
              class="hidden-lg-and-down"
              @click="updatePrimaryKeySearchCriteria(row.item.primary_key)">
              <v-icon>mdi-filter</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:item.action="row">
          {{ $t(row.item.action) }}
        </template>
        <template v-slot:item.timestamp="row">
          <multi-time-display :time="row.item.timestamp" />
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="grey lighten-2">
            <audit-log-details :item="item"></audit-log-details>
          </td>
        </template>
      </v-data-table>
    </div>
  </div>
</template>
<script>

import { map, filter, defaults, debounce, sortBy, each, includes, isEmpty } from 'lodash'
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import VfTooltip from '@/components/shared/VfTooltip'
import IfSet from '@/components/shared/IfSet'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import AuditLogDetails from '@/components/audit-log/AuditLogDetails'
import DatePicker from '@/components/shared/DatePicker'
import Moment from 'moment-timezone'

export default {
  name: 'AuditLog',
  components: {
    VfAutocomplete,
    TablePaginationControls,
    VfTooltip,
    IfSet,
    MultiTimeDisplay,
    AuditLogDetails,
    DatePicker
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin
  ],
  props: {
    /**
     * Whether to autoload the results upon loading.
     */
    autoload: {
      type: Boolean,
      default: false
    },
    /**
     * A list of the table controls to present. The following are supported:
     * - actor
     * - customer
     * - resource_type
     * - primary_key
     * - date_range
     * - export
     *
     * If no value is provided here, all controls will be displayed by default.
     */
    tableControls: {
      type: Array,
      default: () => {
        return []
      }
    },
    /**
     * The table columns to present. The following are supported:
     * - user
     * - resource_type
     * - primary_key
     * - action
     * - timestamp
     *
     * If no value is provided here, all columns will be displayed by default.
     */
    tableColumns: {
      type: Array,
      default: () => {
        return []
      }
    },
    initSearchCriteria: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      model: 'AuditLog',
      searchCriteria: null,
      auditedResources: [],
      remoteDataTableAutoLoad: false,
      userOptions: [],
      loadingUserOptions: false,
      actionOptions: [
        {
          value: 'user_actions',
          text: this.$t('user_actions_only')
        },
        {
          value: 'system_actions',
          text: this.$t('system_actions_only')
        },
        {
          value: 'all',
          text: this.$t('all_actions')
        }
      ]
    }
  },
  computed: {
    tableFields () {
      let result = [
        {
          value: 'user',
          text: this.$t('actor')
        },
        {
          value: 'customer',
          text: this.$tc('customer')
        },
        {
          value: 'resource_type',
          text: this.$t('resource')
        },
        {
          value: 'primary_key',
          text: this.$t('primary_key')
        },
        {
          value: 'action',
          text: this.$tc('action')
        },
        {
          value: 'timestamp',
          text: this.$t('timestamp')
        }
      ]
      // Filter to specified columns in tableColumns. If nothing in there then display everything (no filtering.)
      if (!isEmpty(this.tableColumns)) {
        result = filter(result, field => {
          return includes(this.tableColumns, field.value)
        })
      }
      result.push({
        value: 'data-table-expand',
        text: this.$t('details')
      })
      return result
    },
    rowDetailFields () {
      return [
        {
          value: 'attribute',
          text: this.$tc('attribute')
        },
        {
          value: 'originalValue',
          text: this.$tc('original_value')
        },
        {
          value: 'newValue',
          text: this.$tc('new_value')
        }
      ]
    },
    dateFormatOptions () {
      return {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      }
    },
    customerOptions () {
      const result = map(this.customers, customer => {
        return {
          text: customer.name,
          value: customer.id
        }
      })
      return sortBy(result, 'text')
    },
    resourceTypeOptions () {
      const result = map(this.auditedResources, resource => {
        return {
          text: this.$tc(resource.resource_type),
          value: resource.resource_type
        }
      })
      result.unshift({
        text: this.$t('all'),
        value: null
      })
      return result
    }
  },
  methods: {
    rowClassCallback (item) {
      return (item.index + 1) % 2 === 0 ? 'even' : 'odd'
    },
    /**
     * Returns the default search criteria for this data table
     *
     * @return Object
     */
    getDefaultSearchCriteria () {
      const defaultSearchCriteria = {
        actorEmail: '',
        customers: [],
        createdAfter: Moment().tz(this.customerTimezone).startOf('day').format(),
        createdBefore: null,
        actionType: 'all',
        includeSystemActions: true,
        includeUserActions: true,
        resourceType: null,
        primaryKey: null,
        updatedField: null
      }

      return defaults({}, this.initSearchCriteria, defaultSearchCriteria)
    },

    /**
     * Constructs an object of key value pairs that represent the current search
     * criteria
     *
     * @return object
     */
    buildSearchCriteria () {
      const expand = [
        'customer',
        'user'
      ]
      const fields = [
        'user_id',
        'customer.id',
        'customer.name',
        'resource_type',
        'primary_key',
        'action',
        'old',
        'new',
        'timestamp'
      ]
      const searchCriteria = {
        expand: expand.join(','),
        fields: fields.join(','),
        created_after: this.searchCriteria.createdAfter,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }

      if (this.searchCriteria.resourceType !== null) {
        searchCriteria.resource_type = this.searchCriteria.resourceType
      }

      if (this.searchCriteria.createdBefore !== null) {
        searchCriteria.created_before = this.searchCriteria.createdBefore
      }

      if (this.searchCriteria.customers.length > 0) {
        searchCriteria.customer_id = this.searchCriteria.customers.join(',')
      }

      if (this.searchCriteria.actionType === 'user_actions') {
        searchCriteria.include_system_actions = 0
        searchCriteria.include_user_actions = 1
      }

      if (this.searchCriteria.actionType === 'system_actions') {
        searchCriteria.include_system_actions = 1
        searchCriteria.include_user_actions = 0
        searchCriteria.actor_email_address = undefined
      }

      if (this.searchCriteria.actorEmail && this.searchCriteria.actorEmail.length > 0) {
        searchCriteria.actor_email_address = this.searchCriteria.actorEmail
        // disable include_system_actions if an actor has been specified
        searchCriteria.include_system_actions = 0
      }

      if (this.searchCriteria.primaryKey) {
        searchCriteria.primary_key = this.searchCriteria.primaryKey
      }

      if (this.searchCriteria.updatedField) {
        searchCriteria.updated_field = this.searchCriteria.updatedField
      }

      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }

      return searchCriteria
    },

    /**
     * v-data-table's expand functionality requires us to give some sort of identifier to allow it to differentiate
     * between rows. Audit log's don't have any sort of unique identifier (without us constructing one from say, the
     * timestamp, the resource, and the PK, or something), but assigning each item's index as a property should be
     * sufficient for this purpose.
     *
     * (This uses the RemoteDataTableMixin transformRemoteData handle)
     * @param items
     */
    transformRemoteData (items) {
      each(items, (item, index) => {
        item.index = index
      })
      return items
    },

    /**
     * Return the name of the appropriate icon based on the resource type given
     *
     * @return string
     */
    resourceIcon (resourceType) {
      switch (resourceType) {
        case 'setting':
        case 'user_setting':
        case 'customer_setting':
          return 'mdi-cog'
        case 'location':
          return 'mdi-map-marker'
        case 'product':
        case 'route_product':
          return 'mdi-food'
        case 'route':
          return 'mdi-road-variant'
        case 'user':
        case 'user_customer':
          return 'mdi-account'
        case 'vehicle':
        case 'vehicle_customer':
          return 'mdi-truck'
        case 'route_assessment':
          return 'mdi-flag'
        case 'note':
          return 'mdi-comment'
        case 'product_start_temp':
          return 'mdi-thermometer-low'
        default:
          return 'question'
      }
    },

    /**
     * Creates a link to the given resource (if the type is supported)
     *
     * @return string || undefined
     */
    generateResourceLink (resourceType, primaryKey) {
      // We can only generate links for simple scalar values. If that's not what we have, then return early.
      if (typeof (primaryKey) === 'object') {
        return undefined
      }

      switch (resourceType) {
        case 'customer_setting':
          return 'customer/customer/' + primaryKey + '#general-settings-tab'
        case 'location':
          return 'location/' + primaryKey
        case 'product':
          return 'product/' + primaryKey
        case 'route':
          return 'route/' + primaryKey
        case 'route_product':
          return 'route-product/' + primaryKey
        case 'user':
          return 'user/' + primaryKey
        case 'vehicle':
          return 'vehicle/' + primaryKey
        case 'route_assessment':
          return 'root-cause/' + primaryKey
        default:
          return undefined
      }
    },

    /**
     * Updates the actor search criteria and then refreshes the table
     * @return null
     */
    updateActorEmailSearchCriteria (value) {
      if (value == null) {
        this.searchCriteria.actionType = 'system_actions'
        this.searchCriteria.actorEmail = ''
      } else {
        this.searchCriteria.actionType = 'user_actions'
        this.searchCriteria.actorEmail = value
        this.searchUsers(value)
      }
      this.refreshResults()
    },

    /**
     * Update and refresh the search criteria
     */
    updateCustomerSearchCriteria (customer) {
      this.searchCriteria.customers = [{
        text: customer.name,
        value: customer.id
      }]
      this.initiateSearch()
    },

    /**
     * Update and refresh the search criteria
     */
    updateResourceTypeSearchCriteria (resourceType) {
      this.searchCriteria.resourceType = resourceType
      this.initiateSearch()
    },

    /**
     * Update and refresh the search criteria
     */
    updatePrimaryKeySearchCriteria (primaryKey) {
      if (typeof (primaryKey) === 'object') {
        primaryKey = JSON.stringify(primaryKey)
      }
      this.searchCriteria.primaryKey = primaryKey
      this.initiateSearch()
    },

    showTableControl (controlName) {
      if (isEmpty(this.tableControls)) {
        return true
      }
      return includes(this.tableControls, controlName)
    },

    /**
     * Searches the User API for users with the given email.
     * Debounced to 200ms.
     *
     * @param String query The query to search for.
     */
    searchUsers: debounce(async function (query) {
      if (isEmpty(query)) {
        return
      }
      try {
        this.loadingUserOptions = true
        const response = await this.$orm('User').findAll({
          email: query
        })
        this.userOptions = map(response.items, item => item.email)
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loadingUserOptions = false
      }
    }, 200)
  },
  created () {
    this.searchCriteria = this.getDefaultSearchCriteria()
    // Load the names / types of resources that are audited
    this.$orm('AuditedResource')
      .findAll({})
      .then((collection) => {
        this.auditedResources = collection.getItems()
      })
      .catch(error => {
        this.handleError(error)
      })
  },
  mounted () {
    if (this.autoload) {
      this.initiateSearch()
    }
  },
  watch: {
    'searchCriteria.actionType' (newValue) {
      if (newValue === 'system_actions') {
        this.searchCriteria.actorEmail = null
      }
    }
  }
}
</script>
